'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  plan: string;
}

export default function AuthForm() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupFormData>({ defaultValues: { fullName: '', email: '', password: '', plan: 'standard' } });

  const handleLogin = loginForm.handleSubmit(async () => {
    setIsLoading(true);
    // BACKEND INTEGRATION POINT: POST /api/auth/login with email + password
    await new Promise(r => setTimeout(r, 1200));
    setIsLoading(false);
    setSuccess(true);
    setTimeout(() => { window.location.href = '/main-application-dashboard'; }, 600);
  });

  const handleSignup = signupForm.handleSubmit(async () => {
    setIsLoading(true);
    // BACKEND INTEGRATION POINT: POST /api/auth/register with user data + plan
    await new Promise(r => setTimeout(r, 1200));
    setIsLoading(false);
    setSuccess(true);
    setTimeout(() => { window.location.href = '/main-application-dashboard'; }, 600);
  });

  const ssoProviders = [
    { label: 'Google', icon: 'GlobeAltIcon', color: '#EA4335' },
    { label: 'Microsoft', icon: 'WindowIcon', color: '#00A4EF' },
    { label: 'Enterprise SSO', icon: 'BuildingOfficeIcon', color: '#6C63FF' },
  ];

  return (
    <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
      <div className="w-full" style={{ maxWidth: '440px' }}>
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="font-bold text-lg text-foreground">TableFlex.ai</span>
        </div>

        {/* Tab switcher */}
        <div className="flex glass-card-light rounded-xl p-1 mb-8">
          {(['login', 'signup'] as const).map((m) => (
            <button
              key={`tab-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === m ? 'btn-primary' : 'btn-ghost'}`}
            >
              {m === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          ))}
        </div>

        {/* SSO buttons */}
        <div className="space-y-2 mb-6">
          {ssoProviders.map((provider) => (
            <button
              key={`sso-${provider.label}`}
              className="w-full btn-secondary flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium"
            >
              <Icon name={provider.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-muted-foreground" />
              <span>Continue with {provider.label}</span>
              {provider.label === 'Enterprise SSO' && (
                <span className="ml-auto text-2xs plan-badge-enterprise px-1.5 py-0.5 rounded">SSO</span>
              )}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or with email</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Login form */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Work Email</label>
              <input
                type="email"
                {...loginForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })}
                className="input-field w-full rounded-xl px-3.5 py-2.5 text-sm"
                placeholder="you@company.com"
              />
              {loginForm.formState.errors.email && (
                <p className="text-xs text-danger mt-1">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground">Password</label>
                <button type="button" className="text-xs text-primary hover:underline">Forgot password?</button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...loginForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })}
                  className="input-field w-full rounded-xl px-3.5 py-2.5 text-sm pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 btn-ghost p-0"
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} className="text-muted-foreground" />
                </button>
              </div>
              {loginForm.formState.errors.password && (
                <p className="text-xs text-danger mt-1">{loginForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                {...loginForm.register('remember')}
                className="w-4 h-4 rounded accent-primary"
              />
              <label htmlFor="remember" className="text-sm text-secondary-foreground">Remember me for 30 days</label>
            </div>

            <button
              type="submit"
              disabled={isLoading || success}
              className="w-full btn-primary rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 mt-2"
              style={{ minHeight: '44px' }}
            >
              {isLoading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : success ? (
                <>
                  <Icon name="CheckIcon" size={16} />
                  <span>Success! Redirecting...</span>
                </>
              ) : (
                'Sign In to TableFlex.ai'
              )}
            </button>
          </form>
        )}

        {/* Signup form */}
        {mode === 'signup' && (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Full Name</label>
              <input
                type="text"
                {...signupForm.register('fullName', { required: 'Full name is required' })}
                className="input-field w-full rounded-xl px-3.5 py-2.5 text-sm"
                placeholder="Your full name"
              />
              {signupForm.formState.errors.fullName && (
                <p className="text-xs text-danger mt-1">{signupForm.formState.errors.fullName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Work Email</label>
              <input
                type="email"
                {...signupForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })}
                className="input-field w-full rounded-xl px-3.5 py-2.5 text-sm"
                placeholder="you@company.com"
              />
              {signupForm.formState.errors.email && (
                <p className="text-xs text-danger mt-1">{signupForm.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...signupForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })}
                  className="input-field w-full rounded-xl px-3.5 py-2.5 text-sm pr-10"
                  placeholder="Min. 8 characters"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 btn-ghost p-0">
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} className="text-muted-foreground" />
                </button>
              </div>
              {signupForm.formState.errors.password && (
                <p className="text-xs text-danger mt-1">{signupForm.formState.errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Starting Plan</label>
              <p className="text-xs text-muted-foreground mb-2">You can upgrade at any time</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'basic', label: 'Basic', color: 'plan-badge-basic' },
                  { value: 'standard', label: 'Standard', color: 'plan-badge-standard' },
                  { value: 'enterprise', label: 'Enterprise', color: 'plan-badge-enterprise' },
                ].map((plan) => (
                  <label key={`plan-${plan.value}`} className="cursor-pointer">
                    <input type="radio" {...signupForm.register('plan')} value={plan.value} className="sr-only" />
                    <div className={`text-center py-2 rounded-lg text-xs font-semibold border transition-all duration-150 ${plan.color} ${signupForm.watch('plan') === plan.value ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}`}>
                      {plan.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || success}
              className="w-full btn-primary rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 mt-2"
              style={{ minHeight: '44px' }}
            >
              {isLoading ? (
                <><Icon name="ArrowPathIcon" size={16} className="animate-spin" /><span>Creating account...</span></>
              ) : success ? (
                <><Icon name="CheckIcon" size={16} /><span>Account created!</span></>
              ) : (
                'Create TableFlex Account'
              )}
            </button>

            <p className="text-xs text-muted-foreground text-center">
              By creating an account, you agree to our{' '}
              <button type="button" className="text-primary hover:underline">Terms of Service</button>
              {' '}and{' '}
              <button type="button" className="text-primary hover:underline">Privacy Policy</button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}